import { cn } from "@/lib/cn";
import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ReactNode,
  forwardRef,
} from "react";
import Icon from "../Icon/Icon";

interface CheckboxProps extends ComponentPropsWithoutRef<"input"> {
  onCheckedChange?: (checked: boolean) => void;
  label?: ReactNode;
  indeterminate?: boolean;
  indeterminateIcon?: ReactNode;
  checkedIcon?: ReactNode;
  uncheckedIcon?: ReactNode;
  size?: number;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      size = 14,
      checked,
      onCheckedChange,
      label,
      indeterminate = false,
      indeterminateIcon = <Icon iconName="MinusIcon" colorVariant="primary" />,
      checkedIcon = <Icon iconName="Check" colorVariant="none" />,
      uncheckedIcon,
      disabled,
      ...props
    },
    ref,
  ) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      onCheckedChange?.(event.target.checked);
    };

    const icon = indeterminate
      ? indeterminateIcon
      : checked
        ? checkedIcon
        : uncheckedIcon;

    return (
      <label
        className={cn(
          "relative inline-flex items-center",
          disabled ? "text-neutral-on-fill-disabled" : "cursor-pointer", // not-allowedはデフォルトでは設定されていない
          className,
        )}
      >
        <input
          type="checkbox"
          ref={ref}
          checked={checked}
          onChange={handleChange}
          className={cn("absolute h-0 w-0 opacity-0", className)}
          {...props}
        />
        <div
          className={cn(
            "flex items-center justify-center rounded-sm border border-primary-fill",
            // 優先順位順に条件を整理
            disabled && {
              // disabledの場合: チェック状態に関係なく統一されたスタイル
              "bg-neutral-fill-disabled border-none [&>svg]:text-neutral-on-fill-disabled": true,
              // disabledかつuncheckedの場合のみ追加スタイル
              "bg-transparent outline outline-1 outline-neutral-fill-disabled":
                !checked,
            },
            // disabled以外の通常状態
            !disabled && {
              "bg-primary-fill text-primary-on-fill": checked,
              "bg-primary-fill [&>svg]:text-primary-on-fill": indeterminate,
            },
          )}
          style={{ width: `${size}px`, height: `${size}px` }}
        >
          {icon}
        </div>
        {label && (
          <span className="ml-2 text-label text-primary-on-neutral-fill">
            {label}
          </span>
        )}
      </label>
    );
  },
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
